import _get from 'lodash.get';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import InViewObserver from 'components/InViewObserver';
import PropTypes from 'prop-types';
import s from './AffiliatesContentV2.scss';
import { AFFILIATES_SDK_URL_V2 } from '../../constants';
import { getAffiliatesFeedPlatform } from './affContentUtils';
import { getPaidReferrer } from '../../routes/articleshow_v2/utils/mspUtils';

const getReferrer = isOrganic => {
  const paidReferrer = getPaidReferrer();
  if (paidReferrer) {
    return paidReferrer;
  }
  if (isOrganic) {
    return _get(
      typeof window !== 'undefined' ? window : {},
      'document.referrer',
      '',
    );
  }
  return '';
};

const scriptPromises = {};

const loadScript = () => {
  let scriptPromise = scriptPromises[AFFILIATES_SDK_URL_V2];

  if (scriptPromise) {
    return scriptPromise;
  }

  if (window.til_affiliates_sdk_v2) {
    scriptPromises[AFFILIATES_SDK_URL_V2] = Promise.resolve();
    return scriptPromises[AFFILIATES_SDK_URL_V2];
  }

  scriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(AFFILIATES_SDK_URL_V2);

    if (!script) {
      script = document.createElement('script');
      script.src = AFFILIATES_SDK_URL_V2;
      script.id = AFFILIATES_SDK_URL_V2;
      document.body.appendChild(script);
    }

    script.addEventListener('load', () => {
      setTimeout(() => {
        resolve();
      });
    });
    script.addEventListener('error', e => {
      reject(e);
    });
  });

  scriptPromises[AFFILIATES_SDK_URL_V2] = scriptPromise;
  return scriptPromise;
};

const AffiliatesContentV2 = props => {
  const {
    type,
    id,
    filters,
    data,
    isMobile,
    isPrime,
    isFrmapp,
    className,
    loadingComponent,
    componentProps,
    viewMargin,
    onContentLoaded,
    noShowCallback,
    inViewClass,
    handleOrganic,
    enableWidgetsOnLoad,
    initOnPageLoad,
    isSaleLive,
    affiliateCountry,
    client,
    showWidgetOnPrimeToo = false,
    minHeight,
  } = props;
  const notForPrime = isPrime && !showWidgetOnPrimeToo;

  // if (!type || !(id || data || (filters && filters.pagename))) {
  //   return null;
  // }

  if (!client || !type || notForPrime) {
    return null;
  }

  const containerRef = useRef();
  const [isLoading, setIsLoading] = useState(true);
  const [errorOccurred, setErrorOccurred] = useState(false);
  const [geoInfo, setGeoInfo] = useState({});
  const [inViewCallbackReceived, setInViewCallbackReceived] = useState(false);
  const [SDKScriptLoadPromise, setSDKScriptLoadPromise] = useState();
  const [enableAffiliateWidgets, setEnableAffiliateWidgets] = useState(
    enableWidgetsOnLoad || isSaleLive,
  );

  const loadSDKAndBindEvents = () => {
    // bind to gutter ad show/hide events and
    // trigger library's corresponding size changed event
    // this is being done here, as this needs to be done only once
    // for all affiliate content widgets on the page
    window.addEventListener('GUTTER_AD_TYPE_HANDLING', () => {
      window.dispatchEvent(new Event('AFFILIATES_CONTENT_SIZE_CHANGED'));
    });
    window.addEventListener('GUTTER_CLOSE_AD_TYPE_HANDLING', () => {
      window.dispatchEvent(new Event('AFFILIATES_CONTENT_SIZE_CHANGED'));
    });

    setSDKScriptLoadPromise(loadScript());
  };

  const getOptionsFromProps = () => {
    const options = {
      type,
    };

    if (id) {
      options.id = id;
    } else if (data) {
      options.widgetFeed = data;
    } else {
      options.filters = {
        website: 'toi',
        platform: getAffiliatesFeedPlatform(isMobile, isFrmapp),
        country: geoInfo.country,
        pcheck: isPrime,
        affiliateCountry,
        ...filters,
      };
      if (handleOrganic) {
        options.filters.referrer = getReferrer();
      }
    }

    if (className) {
      options.className = className;
    }

    if (componentProps) {
      options.props = componentProps;
    }
    if (client) {
      options.client = client;
    }
    return options;
  };

  const loadWidget = () => {
    window.til_affiliates_sdk_v2
      .loadWidget(containerRef.current, getOptionsFromProps())
      .then(response => {
        onContentLoaded(response);
        setIsLoading(false);
      })
      .catch(() => {
        noShowCallback();
        setIsLoading(false);
        setErrorOccurred(true);
      });
  };

  const loadContent = () => {
    SDKScriptLoadPromise.then(() => {
      if (window.til_affiliates_sdk_v2) {
        loadWidget();
      } else {
        setErrorOccurred(true);
      }
    }).catch(() => {
      setErrorOccurred(true);
    });
  };

  const scrollHandler = useCallback(() => {
    if (window.scrollY > 200) {
      setEnableAffiliateWidgets(true);
      window.removeEventListener('scroll', scrollHandler);
    }
  }, []);

  useEffect(() => {
    if (
      !notForPrime &&
      window.TimesGDPR &&
      window.TimesGDPR.common.consentModule.gdprCallback
    ) {
      window.TimesGDPR.common.consentModule.gdprCallback(dataObj => {
        // we are doing this if country is not present in gdpr, read it from window.geoinfo
        // eslint-disable-next-line camelcase
        const { region_code = '', Continent = '', CountryCode = '' } =
          window.geoinfo || {};

        setGeoInfo({
          country: dataObj.userCountry || CountryCode.toLowerCase(),
          continent: dataObj.userContinent || Continent.toLowerCase(),
          region: dataObj.userRegion || region_code.toLowerCase(),
        });
      });
    }
    if (!enableWidgetsOnLoad && !isSaleLive) {
      window.addEventListener('scroll', scrollHandler);
    }
  }, []);

  useEffect(
    () => {
      if (!notForPrime) {
        if (initOnPageLoad) {
          setInViewCallbackReceived(true);
        }
        if (enableAffiliateWidgets) {
          loadSDKAndBindEvents();
        }
      }
    },
    [enableAffiliateWidgets],
  );

  useEffect(
    () => {
      if (
        !notForPrime &&
        geoInfo.country &&
        inViewCallbackReceived &&
        SDKScriptLoadPromise
      ) {
        loadContent();
      }
    },
    [geoInfo.country, inViewCallbackReceived, SDKScriptLoadPromise],
  );

  if (errorOccurred && notForPrime) {
    return null;
  }
  const loaderStyle = {};
  if (minHeight) {
    loaderStyle.minHeight = minHeight;
  }

  return (
    <InViewObserver
      inViewCallback={() => {
        setInViewCallbackReceived(true);
      }}
      viewMargin={viewMargin || '1000px 0px 1000px 0px'}
      className={inViewClass}
    >
      {isLoading && React.isValidElement(loadingComponent) && loadingComponent}
      <div
        ref={containerRef}
        data-aff-type="aff-content"
        data-aff-version="v2"
        style={loaderStyle}
        className={
          isLoading && !React.isValidElement(loadingComponent)
            ? s.aff_loading
            : ''
        }
      />
    </InViewObserver>
  );
};

AffiliatesContentV2.propTypes = {
  type: PropTypes.string.isRequired,
  id: PropTypes.string,
  filters: PropTypes.shape({
    // website: PropTypes.string,  -- set as toi hard coded
    // platform: PropTypes.string, -- identified based on isMobile and isFrmapp
    // pcheck: PropTypes.string, -- identified based on isPrime
    // country: PropTypes.string, -- identified from gdpr callback
    pagename: PropTypes.string,
    version: PropTypes.string,
    sectionl1: PropTypes.string,
    keywords: PropTypes.string,
    lang: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    // productId and affiliateName are for individual product card widgets
    productId: PropTypes.string,
    affiliateName: PropTypes.string,
    imgid: PropTypes.string,
  }),
  data: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  isPrime: PropTypes.bool,
  isFrmapp: PropTypes.bool,
  handleOrganic: PropTypes.bool,
  className: PropTypes.string,
  inViewClass: PropTypes.string,
  viewMargin: PropTypes.string,
  loadingComponent: PropTypes.node,
  componentProps: PropTypes.shape({}),
  onContentLoaded: PropTypes.func,
  noShowCallback: PropTypes.func,
  enableWidgetsOnLoad: PropTypes.bool,
  showWidgetOnPrimeToo: PropTypes.bool,
  initOnPageLoad: PropTypes.bool,
  isSaleLive: PropTypes.bool,
  affiliateCountry: PropTypes.string,
  minHeight: PropTypes.string,
  client: PropTypes.string,
};

AffiliatesContentV2.defaultProps = {
  id: undefined,
  filters: undefined,
  data: undefined,
  isMobile: false,
  isPrime: false,
  isFrmapp: false,
  handleOrganic: false,
  className: '',
  inViewClass: '',
  loadingComponent: undefined,
  componentProps: undefined,
  viewMargin: '',
  onContentLoaded: () => {},
  noShowCallback: () => {},
  enableWidgetsOnLoad: false,
  showWidgetOnPrimeToo: false,
  initOnPageLoad: false,
  isSaleLive: false,
  affiliateCountry: '',
  minHeight: '',
  client: undefined,
};

const mapStateToProps = state => ({
  isPrime: state.isPrime,
  isMobile: state.isMobile,
  isFrmapp: state.isFrmapp,
  isSaleLive:
    _get(
      state,
      'common.siteConfig.affiliates.saleStatus.isIndiaSaleLive',
      'false',
    ) === 'true',
});

export default connect(mapStateToProps)(AffiliatesContentV2);
